import { runCodeEditorSnippetThunk } from '../../../../../store/exam/thunks';
import { CodingExamQuestion } from '../../../../../store/exam/types';
import { PlayIcon } from '../../../../../assets/icons/common';
import { useDispatch } from '../../../../../store/store';
import Button from '../../../../../components/Button';
import { useEffect, useRef, useState } from 'react';
import type * as monaco from 'monaco-editor';
import { FILE_NAMES } from '../../constants';
import Editor from '@monaco-editor/react';
import styles from './style.module.scss';

interface Props {
    showMore: boolean;
    question: CodingExamQuestion;
    onChange: (answer: string) => void;
};

/** The editor the answer is written in, with the output of the last run beside it. */
function CodeEditor(props: Props) {
    const { showMore, question, onChange } = props;

    const dispatch = useDispatch();
    const [running, setRunning] = useState(false);
    const [output, setOutput] = useState<string | null>(null);
    const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

    const fileName = FILE_NAMES[question.language] || '';

    useEffect(() => {
        // A new question starts from a blank editor and an empty console.
        editorRef.current?.setValue('');
        setOutput(null);
    }, [question]);

    const runCode = async () => {
        if (!editorRef.current) return;
        setRunning(true);

        try {
            const result = await dispatch(runCodeEditorSnippetThunk(JSON.stringify({
                language: question.language,
                files: [{ name: fileName, content: editorRef.current.getValue() }],
            }))).unwrap();

            setOutput(result);
        } catch {
            // The compiler is a third party — leave the previous output on screen.
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className={styles.bottom_section}>
            <div className={styles.bottom_section_header}>
                <span className={styles.bottom_section_header_title}>{fileName}</span>
                <Button
                    text='Run'
                    type='Error'
                    onClick={runCode}
                    loading={running}
                    icon={<PlayIcon />}
                    className={styles.bottom_section_header_btn}
                />
            </div>
            <div className={styles.bottom_section_editor}>
                <Editor
                    theme='vs-dark'
                    defaultValue=""
                    className={styles.code_editor}
                    defaultLanguage={question.language}
                    onChange={value => onChange(value || '')}
                    onMount={editor => { editorRef.current = editor }}
                    height={showMore ? 'calc(100vh - 472px)' : 'calc(100vh - 302px)'}
                />
                <div className={styles.bottom_section_editor_output}>
                    <p>
                        Output:<br /><br />
                        {output}
                    </p>
                </div>
            </div>
        </div>
    )
};

export default CodeEditor;
